<template>
  <div class="custom-content uk-margin uk-card uk-card-default uk-card-body">
    <device-type-message class="uk-visible@m" v-if="deviceRequired"></device-type-message>
    <div v-if="sharePreview">
      <p><img class="lock-svg" :src='lockIcon'>{{$t('contentInformation.privatelyShared')}}</p>
      <hr>
      <p>
        {{$t('contentInformation.thisResourceHas')}}
        <br>
        {{$t('contentInformation.itCanOnly')}}
      </p>
    </div>
    <div v-if="!sharePreview">
      <div class="uk-flex uk-flex-left">
        <div id="contentDescription" class="content-description">
          <span class="description-links" v-html="description"></span>
        </div>
      </div>
      <div class="uk-width-1-1 uk-margin-top">
        <div class="uk-margin-small-right uk-display-inline-block" v-for="type in info.componentTypes">
          <img :src="resolveIcon(type)"/>
          <a class="content-link" @click="applyComponentTypeFilter(type)">{{resolveType(type)}}</a>
        </div>
      </div>
      <div class="uk-margin-top">
        <span v-if="defaultLanguage" class="content-heading">{{$t('general.grades')}}:</span>
        <span v-else class="content-heading">{{$t('general.ages')}}:</span>
        <div class="uk-display-inline-block" v-for="(range,index) in ranges">
          <a class="content-link" @click="applyGradeFilter(range.levels)">
           <span>{{resolveLevels(range)}}</span>
          </a>
          <span v-if="ranges[index+1]" class="content-separator-color content-filters-separator">;</span>
        </div>
      </div>
      <div class="uk-margin-top">
        <span class="content-heading">{{$t('general.subjects')}}:</span>
        <div class="uk-display-inline-block" v-for="(subject,index) in subjects">
          <a @click="applySubjectFilter(subject)" class="content-link bold">{{subject}}</a><span
          v-if="subjects[index+1]" class="content-separator-color">,</span>&nbsp;
        </div>
      </div>
      <div v-if="keywords(info.tags)" class="uk-margin-top">
        <span class="content-heading">{{$t('general.keywords')}}:</span>
        <div class="uk-display-inline-block" v-for="(tag,index) in info.tags">
          <a @click="applyKeywordFilter(tag)" class="content-link bold">{{tag}}</a><span v-if="info.tags[index+1]"
                                                                                         class="content-separator-color">,</span>&nbsp;
        </div>
      </div>
      <div class="uk-margin-top" v-if="set">
        <span class="content-link bold content-heading">{{$t('general.standard')}}:</span>
        <span class="bold content-link uk-display-inline-block add-pointer" @click="applyStandardsFilter(info.standardSets[0])">{{set}}- </span>
        <div class="uk-display-inline-block add-pointer" @click="applyStrandFilter(info.standardSets[0], strand)" v-for="(strand,index) in strands">
          <tooltip :separator="strands[index+1]" :text="strand.standard" :tooltipText="strand.description"
                   v-if="strand.description"></tooltip>
        </div>
      </div>
      <div v-if="info.country" class="uk-margin-top">
        <span class="content-heading">{{$t('general.location')}}:</span>
          <a class="content-link bold" @click="applyLocationFilter(info.country)">{{resolveCountry(info.country)}}</a>&nbsp;
      </div>
      <hr>
      <div class="uk-margin-top">
        <div class="uk-display-inline-block">
          <a @click="claimAuthorship()" class="content-link-light">{{$t('contentInformation.iAmTheAuthor')}}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import rangeBuilder from '../../utils/range-builder-util'
  import gradeUtil from '../../utils/grade-util'
  import ageUtil from '../../utils/age-util'
  import urlReplace from '../../utils/url-helpers'
  import resourceUtil from '../../utils/resource-type-util'
  import DeviceTypeMessage from './device-type-message'
  import Router from '../../router/index'
  import {mapActions, mapGetters, mapMutations} from 'vuex'
  import * as event from '../../enums/mixpanel-event'
  import iconUtil from '../../utils/icon-util'
  import * as misc from '../../enums/misc'
  import mixpanel from '../../services/mixpanel'
  import * as supportedCountries from '../../enums/supported-countries'
  import subjectUtil from '../../utils/subject-translator-util'
  import * as _ from 'lodash'
  import tooltip from '../ui-components/tooltip/tooltip'

  export default {
    components: {DeviceTypeMessage, tooltip},
    name: 'content-information',
    props: ['set', 'strands', 'info', 'device-required', 'sharePreview'],
    data() {
      return {
        lockIcon: iconUtil.resolve(misc.LOCK_ICON),
        defaultLanguage: this.$i18n.locale === 'en' || this.$i18n.locale === 'en-US'
      }
    },
    methods: {
      ...mapActions(['updateSelectedFilters', 'setDefaultFilters', 'updateKeywordFilter', 'cleanSetSearchCriteria']),
      ...mapMutations(['setStrands', 'setSearchStrands']),
      resolveIcon(type) {
        try {
          return iconUtil.resolve(type)
        } catch (err) {
        }
      },
      resolveGrade(grade) {
        try {
          return gradeUtil.resolve(grade)
        } catch (err) {}
      },
      resolveAge(level, range) {
        if (range) {
          return ageUtil.resolve(level, range)
        } else {
          return ageUtil.resolve(level)
        }
      },
      resolveLevels(range) {
        let levels = ''
        if (this.defaultLanguage) {
          if (range.high !== undefined) {
            levels = this.resolveGrade(range.low) + ' to ' + this.resolveGrade(range.high)
          } else {
            levels = this.resolveGrade(range.low)
          }
        } else {
          if (range.high !== undefined) {
            levels = this.resolveAge(range.low) + ' - ' + this.resolveAge((range.high < 13) ? range.high + 1 : range.high)
          } else {
            levels = this.resolveAge(range.low, true)
          }
        }
        return levels
      },
      applyComponentTypeFilter(type) {
        this.setDefaultFilters(this.filters)
        this.cleanSetSearchCriteria(null)
        this.updateSelectedFilters({filters: this.filters, filterId: type, filterGroup: 'Resource Type'})
        Router.push('/search')
      },
      applySubjectFilter(subject) {
        this.setDefaultFilters(this.filters)
        this.cleanSetSearchCriteria(null)
        this.updateSelectedFilters({filters: this.filters, filterId: subject, filterGroup: 'Subjects'})
        Router.push('/search')
      },
      applyGradeFilter(grades) {
        this.setDefaultFilters(this.filters)
        this.cleanSetSearchCriteria(null)
        for (let grade of grades) {
          this.updateSelectedFilters({filters: this.filters, filterId: grade, filterGroup: 'Grades'})
        }
        Router.push('/search')
      },
      applyKeywordFilter(keyword) {
        this.cleanSetSearchCriteria(null)
        this.updateKeywordFilter(keyword)
        Router.push('/search')
      },
      applyLocationFilter(location) {
        this.setDefaultFilters(this.filters)
        this.cleanSetSearchCriteria(null)
        this.updateSelectedFilters({filters: this.filters, filterId: location, filterGroup: 'Location'})
        Router.push('/search')
      },
      applyStandardsFilter(standard) {
        this.setDefaultFilters(this.filters)
        this.cleanSetSearchCriteria(null)
        this.updateSelectedFilters({filters: this.filters, filterId: standard, filterGroup: 'Standards'})
        Router.push('/search')
      },
      applyStrandFilter(standard, strand) {
        let selectedStrands = []
        let selectedStrand = {
          title: strand.standard,
          id: strand.guid,
          standard: strand.standard,
          description: strand.description,
          selected: true
        }
        selectedStrands.push(selectedStrand)
        this.setDefaultFilters(this.filters)
        this.cleanSetSearchCriteria(null)
        this.updateSelectedFilters({filters: this.filters, filterId: standard, filterGroup: 'Standards'})
        this.setStrands(selectedStrands)
        let searchStrands = []
        searchStrands.push(selectedStrand)
        let noneStrand = {title: this.$t('general.none'), id: 'none', selected: false}
        searchStrands.unshift(noneStrand)
        this.setSearchStrands(searchStrands)
        Router.push('/search')
      },
      resolveType(type) {
        return resourceUtil.resolve(type, this.$t('resourceTypes'))
      },
      claimAuthorship() {
        this.$router.push({name: 'Authorship', params: {id: this.info.contentId, fileName: this.info.fileName}})
        mixpanel.track(event.CLAIMED_AUTHORSHIP, {contentId: this.$route.params.id})
      },
      resolveCountry(id) {
        let countryTitle = supportedCountries.SUPPORTED_COUNTRIES.find(o => o.ID === id).title
        let countries = this.$t('countries')
        return countries[countryTitle]
      },
      resolveSubject(id) {
        return subjectUtil.resolve(id, this.$t('subjects'))
      },
      keywords(tags) {
        return _.size(tags)
      }
    },
    computed: {
      ...mapGetters(['filters', 'keyword']),
      ranges() {
        let gradeRanges = []
        try {
          for (let grade of this.info.grades) {
            let range = rangeBuilder.buildRanges(grade.levels)
            for (let rng of range) {
              gradeRanges.push(rng)
            }
          }
        } catch (err) {
          gradeRanges = []
        }
        return gradeRanges
      },
      description() {
        if (this.info.description) {
          return urlReplace(this.info.description, misc.LINK_STYLE)
        }
        return ''
      },
      subjects() {
        let masterSubjects = []
        try {
          for (let subject of this.info.subjects) {
            if (subjectUtil.resolveToMasterSubjectList(subject, this.$t('subjects')) !== undefined) {
              masterSubjects.push(subjectUtil.resolveToMasterSubjectList(subject, this.$t('subjects')))
            }
          }
        } catch (err) {
          masterSubjects = []
        }
        return [...new Set(masterSubjects)]
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../../styles/colours';
  @import '../../styles/breakpoints';
  @import '../../styles/padding';

  .vertical-divider {
    width: 1px;
    height: 35px;
    border-left: solid 1px #e4e4e4;
    display: inline-block;
    margin: -10px 18px
  }

  .lock-svg {
    margin: 0px 14.3px 6px 0px;
  }

  #report-modal {
    .uk-modal-title {
      font-size: 24px;
      font-weight: bold;
      color: rgba(0, 1, 2, 0.7);
    }

    .uk-modal-body {
      width: 900px;
    }

    .uk-button {
      width: 235px;
      height: 45px;
      font-size: 18px;
      text-transform: capitalize;
    }
  }

  .uk-notification-message {
    color: red !important;
  }

  .uk-tooltip {
    color: red;
  }

  .description-links {
    word-wrap: break-word;

    a {
      font-size: 14px;
      color: $content-link-blue;
      font-weight: bold;
    }
  }

  .uk-link {
    font-size: 14px;
    color: $content-link-blue;
    font-weight: bold;
  }
  .content-description {
    font-size: 16px;
    white-space: pre-line;
    word-break: break-word;
    -webkit-hyphens: auto;
    -moz-hyphens: auto;
    -ms-hyphens: auto;
    hyphens: auto;
  }

  .content-separator-color {
    color: $content-separator-slate;
  }

  .content-filters-separator {
    margin-left: -4px;
    margin-right: 4px;
  }

  .content-link {
    font-size: 14px;
    color: $content-link-blue;
    font-weight: bold;
  }

  .content-link-light {
    font-size: 14px;
    color: $content-link-blue;
  }

  .uk-card-body {
    padding: $small-content-padding;
    color: $dark-text;
  }

  .uk-margin {
    margin-top: 16px !important;
  }

  .custom-content {
    margin-bottom: 32px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.24);
    border-radius: 2px;
  }

  .content-heading {
    font-size: 14px;
    font-weight: bold;
    color: black;
  }

  @media screen and (min-width: $breakpoint-small) {
    .content-description {
      font-size: 18px;
    }
  }

  @media screen and (max-width: $breakpoint-mobile) {
    .content-description {
      font-size: 14px;
    }
    .content-link {
      line-height: 1.71;
    }
    .content-text {
      font-size: 14px;
    }
  }

  .uk-card-body {
    padding: 24px 32px;;
  }
</style>
